import { useEffect, useState } from "react";
import { initGtag } from "@/lib/gtag";
import { initMetaPixel } from "@/lib/metaPixel";

/**
 * Samtykke til informasjonskapsler.
 *
 * Google Analytics skriver _ga og Meta-pikselet skriver _fbp. Ingen av dem
 * skal skrives foer den besoekende har sagt ja, jamfoer ekomloven paragraf
 * 3-15. Derfor lastes ingenting fra Google eller Meta foer et trykk paa
 * «Godta alle», og valget huskes i tolv maaneder. Sier de nei, spoer vi ikke
 * igjen i samme periode, og maalingen lastes aldri.
 */

const NOKKEL = "drammencarwash.samtykke";
const TOLV_MANEDER = 365 * 24 * 60 * 60 * 1000;
const PERSONVERN = "/personvern";

type Valg = { godtatt: boolean; tid: number };

function lesValg(): Valg | null {
  try {
    const raw = localStorage.getItem(NOKKEL);
    if (!raw) return null;
    const v = JSON.parse(raw) as Valg;
    if (typeof v?.godtatt !== "boolean" || typeof v?.tid !== "number") return null;
    if (Date.now() - v.tid > TOLV_MANEDER) return null;
    return v;
  } catch {
    return null;
  }
}

function skrivValg(godtatt: boolean) {
  try {
    localStorage.setItem(NOKKEL, JSON.stringify({ godtatt, tid: Date.now() }));
  } catch {
    // Privat vindu eller blokkert lagring. Da spoer vi igjen neste gang, som
    // er riktigere enn aa anta et ja.
  }
}

function lastMaaling() {
  initGtag();
  initMetaPixel();
}

export function Samtykke() {
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const valg = lesValg();
    if (valg === null) {
      setVis(true);
      return;
    }
    if (valg.godtatt) lastMaaling();
  }, []);

  if (!vis) return null;

  const svar = (godtatt: boolean) => {
    skrivValg(godtatt);
    setVis(false);
    if (godtatt) lastMaaling();
  };

  return (
    <div
      role="dialog"
      aria-label="Informasjonskapsler"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 p-4 backdrop-blur md:p-5"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-muted-foreground">
          Vi bruker informasjonskapsler til å se hvordan siden brukes. Du velger selv.{" "}
          <a href={PERSONVERN} className="underline hover:text-foreground">
            Les personvernerklæringen
          </a>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => svar(false)}
            className="min-h-[44px] rounded-xl border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Bare nødvendige
          </button>
          <button
            type="button"
            onClick={() => svar(true)}
            className="min-h-[44px] rounded-xl bg-primary px-5 text-sm font-bold text-primary-foreground"
          >
            Godta alle
          </button>
        </div>
      </div>
    </div>
  );
}
